import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchModules, fetchLearningPaths, fetchTaxonomy } from '../services/apiService';

const CatalogContext = createContext(null);

export function CatalogProvider({ children }) {
  const [modules, setModules] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ levels: [], products: [], roles: [], subjects: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const loadTaxonomy = useCallback(async () => {
    try {
      const data = await fetchTaxonomy();
      setTaxonomy({
        levels:   data.levels   || [],
        products: data.products || [],
        roles:    data.roles    || [],
        subjects: data.subjects || [],
      });
    } catch (err) {
      console.error('Taxonomy load failed', err);
    }
  }, []);

  const loadModules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchModules();
      setModules(data.modules || []);
    } catch (err) {
      setError(err.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLearningPaths = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLearningPaths();
      setLearningPaths(data.learningPaths || []);
    } catch (err) {
      setError(err.message || 'Failed to load learning paths');
    } finally {
      setLoading(false);
    }
  }, []);

  const initialize = useCallback(async () => {
    if (initialized) return;
    setLoading(true);
    try {
      await loadTaxonomy();
      setInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [initialized, loadTaxonomy]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <CatalogContext.Provider value={{
      modules, setModules, loadModules,
      learningPaths, setLearningPaths, loadLearningPaths,
      taxonomy, loadTaxonomy,
      loading, error, initialized,
    }}>
      {children}
    </CatalogContext.Provider>
  );
}

export const useCatalog = () => {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog must be inside CatalogProvider');
  return ctx;
};
